import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Users } from 'lucide-react';
import type { SocialAccount } from '@/types/social';
import { NETWORK_LABELS } from '@/types/social';
import { accountLabel, groupAccountsByNetwork } from '@/utils/social';
import { NetworkIcon } from './SocialBits';

/** Foto do perfil (a da rede, quando lida) com o ícone da rede no canto. */
export const AccountAvatar: React.FC<{ account?: SocialAccount; size?: 'sm' | 'md' | 'lg'; className?: string }> = ({
  account,
  size = 'sm',
  className,
}) => {
  const [broken, setBroken] = useState(false);
  const box = { sm: 'w-6 h-6', md: 'w-10 h-10', lg: 'w-20 h-20 sm:w-24 sm:h-24' }[size];
  const initial = (account?.display_name || account?.username || '?').charAt(0).toUpperCase();
  return (
    <span className={clsx('relative inline-flex flex-shrink-0', box, className)}>
      {account?.avatar_url && !broken ? (
        <img src={account.avatar_url} alt="" className="w-full h-full rounded-full object-cover bg-slate-100" onError={() => setBroken(true)} />
      ) : (
        <span
          className={clsx(
            'w-full h-full rounded-full bg-gradient-to-tr from-amber-400 to-pink-600 text-white font-semibold flex items-center justify-center',
            size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-base' : 'text-[11px]',
          )}
        >
          {initial}
        </span>
      )}
      {account && size !== 'lg' && (
        <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-px shadow">
          <NetworkIcon network={account.network} size={size === 'sm' ? 9 : 12} />
        </span>
      )}
    </span>
  );
};

interface ProfileSwitcherProps {
  accounts: SocialAccount[];
  value: number | null;
  onChange: (id: number | null) => void;
  /** Mostra a opção "Todos os perfis" (listas); a Grade é sempre de um perfil. */
  allowAll?: boolean;
  className?: string;
}

/**
 * Troca de perfil das Redes sociais (estilo Plann): chips com a foto e o @, agrupados por rede.
 * Documentação: src/docs/redes-sociais/grade.md.
 */
export const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({ accounts, value, onChange, allowAll, className }) => {
  const list = groupAccountsByNetwork(accounts).flatMap((g) => g.accounts);
  if (list.length === 0 || (list.length === 1 && !allowAll)) return null;
  const chip = (active: boolean) =>
    clsx(
      'inline-flex items-center gap-1.5 rounded-full border pl-1 pr-3 py-1 text-xs whitespace-nowrap transition-colors',
      active ? 'bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
    );
  return (
    <div className={clsx('flex gap-2 overflow-x-auto custom-scrollbar pb-1', className)} role="radiogroup" aria-label="Perfil">
      {allowAll && (
        <button type="button" role="radio" aria-checked={value === null} className={chip(value === null)} onClick={() => onChange(null)}>
          <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
            <Users size={13} className="text-slate-500" />
          </span>
          Todos os perfis
        </button>
      )}
      {list.map((a) => (
        <button
          key={a.id}
          type="button"
          role="radio"
          aria-checked={value === a.id}
          title={`${NETWORK_LABELS[a.network]} · ${a.display_name || a.username}`}
          className={chip(value === a.id)}
          onClick={() => onChange(a.id)}
        >
          <AccountAvatar account={a} />
          {accountLabel(a)}
        </button>
      ))}
    </div>
  );
};
